import React, { useContext, useMemo } from 'react';
import Styled from 'styled-components';
import Link from 'next/link';
import { Trans } from '@lingui/react';
import { addMinutes, format } from 'date-fns';
import { sv, nb, fi, enGB } from 'date-fns/locale';
import ProgressIcon from './ProgressIcon';
import LightningIcon from '@assets/LightingIcon';
import ArrowRightIcon from '@assets/ArrowRightIcon';
import LanguageContext from '@lib/contexts/languageContext';
import { LIVE_KINDS } from '@lib/constants';
import {
  LiveEventEssentialFieldsFragment,
  LiveEventKind,
} from '@gql/generated';
import Image from 'next/image';

const Card = Styled.a<{ expired?: boolean }>`
  display: flex;
  flex-direction: column;
  color: #243240;
  text-decoration: none;
  border-radius: 4px;
  padding: 20px 15px;
  background-color: #F0F5FB;
  overflow: hidden;

  ${({ expired }) => (expired ? 'opacity: 0.7;' : '')}
`;

const Head = Styled.div`
  display: grid;
  grid-template-areas: "Date Booking"
                       "Month Time";
  font-size: 14px;
  color: #243240;
  line-height: 1.2;
  margin-bottom: 20px;
`;

const EventMonth = Styled.span`
  grid-area: Month;
  text-transform: capitalize;
`;

const EventDate = Styled.span`
  grid-area: Date;
  font-size: 32px;
  font-weight: 600;
`;

const Time = Styled.span`
  grid-area: Time;
  text-align: right;
`;

const BookingStatus = Styled.p`
  grid-area: Booking;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  text-align: right;
  text-transform: uppercase;
  margin: 0;
  
  svg {
    margin-right: 5px;
  }
`;

const Body = Styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 345px;
`;

const ImageWrapper = Styled.div`
  position: relative;
  display: flex;
  margin-bottom: 20px;

  :before {
    content: "";
    display: block;
    width: 100%;
    padding-top: 50%;
  }
`;

const Tags = Styled.div`
  position: absolute;
  left: 8px;
  bottom: 8px;
  display: flex;
`;

const Tag = Styled.p<{ color?: string }>`
  font-size: 11px;
  ${({ color }) => (color ? `color: ${color};` : '')}
  font-weight: 400;
  text-transform: uppercase;
  border-radius: 3px;
  padding: 5px;
  margin: 0 5px 0 0;
  background-color: white;

  :empty {
    display: none;
  }
`;

const Img = Styled(Image)`
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  width: 100%;
  object-fit: cover;
  border-radius: 4px;
`;

const Title = Styled.p`
  font-size: 24px;
  font-weight: 600;
  line-height: 1.3;
  margin: 0 0 10px;
`;

const InstructorName = Styled.p`
  font-size: 14px;
  margin: 0;
`;

const Price = Styled.p`
  flex: 1;
  display: flex;
  align-items: flex-end;
  font-size: 16px;
  margin-bottom: 15px;
`;

const Footer = Styled.div`
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #213E3D;
  font-weight: 500;
  border-bottom: 5px solid #BECCD9;
  padding: 0 20px 0 15px;
  margin: 0 -15px -20px -15px;
  background-color: #D5E0EC;
  cursor: pointer;

  span {
    flex: 1;
    margin-left: 10px;
  }
`;

const localeMap = {
  en: enGB,
  se: sv,
  no: nb,
  fi,
};

type Props = {
  className?: string;
  event: LiveEventEssentialFieldsFragment;
  hasAccess: boolean;
};

export const getFormattedPrice = (event: LiveEventEssentialFieldsFragment) => {
  const price = event.price;
  let amount = price.originalPrice;
  if (price.discountedPrice !== null) {
    amount = price.discountedPrice;
  }
  return `${Math.round(amount / 100).toFixed(2)} ${price.currency}`;
};

export const getEventSpotsStatus = (
  event: LiveEventEssentialFieldsFragment
) => {
  if (event.isExpired) {
    return <Trans id="live.event_page.expired_event" />;
  } else if (event.isComingSoon) {
    return <Trans id="live.event_page.coming_soon" />;
  } else if (event.availableSpots === 0) {
    return <Trans id="live.event_page.fully_booked_event" />;
  } else if (event.availableSpots <= 10) {
    return (
      <Trans
        id="live.event_page.spots_left"
        values={{ spots: event.availableSpots }}
      />
    );
  }

  return null;
};

const EventCard = ({ className = '', event, hasAccess }: Props) => {
  const lang = useContext(LanguageContext);
  const eventDate = new Date(event.eventDates[0]);
  const day = format(eventDate, 'dd');
  const month = format(eventDate, 'MMMM', { locale: localeMap[lang] });

  const percentage =
    ((event.maximumParticipants - event.availableSpots) * 100) /
    event.maximumParticipants;

  const startTime = format(eventDate, 'HH:mm');
  const endTime = format(addMinutes(eventDate, event.duration), 'HH:mm OOO');

  const priceText = useMemo(() => {
    if (event.kind === LiveEventKind['DonationClass']) {
      return <Trans id="live.event_page.donation_class" />;
    } else if (event.price.originalPrice === 0) {
      return <Trans id="live.event_page.event_free" />;
    } else if (event.price.discountedPrice === 0) {
      return <Trans id="live.event_page.for_for_you" />;
    } else {
      return getFormattedPrice(event);
    }
  }, [event?.id]);

  return (
    <Link href={`/${lang}/live/${event.slug}`} prefetch={false} legacyBehavior>
      <Card
        className={className}
        href={`/${lang}/live/${event.slug}`}
        expired={event.isExpired}
        data-testid="EventCard"
      >
        <Head>
          <EventDate>{day}</EventDate>
          <EventMonth>{month}</EventMonth>
          <BookingStatus>
            {!event.isExpired && getEventSpotsStatus(event) ? (
              <ProgressIcon percentage={percentage} />
            ) : null}
            {getEventSpotsStatus(event)}
          </BookingStatus>
          <Time>
            {startTime} - {endTime}
          </Time>
        </Head>
        <Body>
          <ImageWrapper>
            <Img src={event.thumbnail} alt="" width={275} height={140} />
            <Tags>
              <Tag>{LIVE_KINDS[event.kind]}</Tag>
              {hasAccess ? (
                <Tag color="#32847E">
                  <Trans id="live.event_page.you_r_in_event" />
                </Tag>
              ) : null}
            </Tags>
          </ImageWrapper>
          <Title>{event.title}</Title>
          <InstructorName>{event.instructors?.[0]?.name}</InstructorName>
          <Price>{priceText}</Price>
        </Body>
        <Footer>
          <LightningIcon />
          <span>{event.theme}</span>
          <ArrowRightIcon />
        </Footer>
      </Card>
    </Link>
  );
};

export default EventCard;
